import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { AdminType, CreateAdminRequest } from '../../types/auth';

interface CreateAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: CreateAdminRequest) => void;
  loading?: boolean;
}

const CreateAdminModal: React.FC<CreateAdminModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [adminType, setAdminType] = useState<AdminType>(AdminType.ADMIN);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setName('');
      setPhone('');
      setEmail('');
      setAdminType(AdminType.ADMIN);
      setLogin('');
      setPassword('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isFormValid()) {
      onSubmit({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        adminType,
        login: login.trim(),
        password: password.trim()
      });
    }
  };

  const handleClose = () => {
    setName('');
    setPhone('');
    setEmail('');
    setAdminType(AdminType.ADMIN);
    setLogin('');
    setPassword('');
    onClose();
  };

  const isFormValid = () => {
    return (
      name.trim() !== '' &&
      phone.trim() !== '' &&
      email.trim() !== '' &&
      email.includes('@') &&
      login.trim() !== '' &&
      password.trim() !== '' &&
      password.length >= 6
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('createAdmin.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="name">{t('createAdmin.fullName')}</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('createAdmin.fullNamePlaceholder')}
            disabled={loading}
            required
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="phone">{t('createAdmin.phone')}</label>
          <input
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('createAdmin.phonePlaceholder')}
            disabled={loading}
            required
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="email">{t('createAdmin.email')}</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('createAdmin.emailPlaceholder')}
            disabled={loading}
            required
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="adminType">{t('createAdmin.adminType')}</label>
          <select
            id="adminType"
            value={adminType}
            onChange={(e) => setAdminType(e.target.value as AdminType)}
            disabled={loading}
            required
          >
            <option value={AdminType.ADMIN}>{t('createAdmin.admin')}</option>
            <option value={AdminType.SUPER_ADMIN}>{t('createAdmin.superAdmin')}</option>
          </select>
        </div>

        <div className="modal-form-group">
          <label htmlFor="login">{t('createAdmin.login')}</label>
          <input
            type="text"
            id="login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder={t('createAdmin.loginPlaceholder')}
            disabled={loading}
            required
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="password">{t('createAdmin.password')}</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('createAdmin.passwordPlaceholder')}
            disabled={loading}
            required
            minLength={6}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            {t('createAdmin.passwordHint')}
          </small>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="cancel-button"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={loading || !isFormValid()}
          >
            {loading ? t('createAdmin.creating') : t('createAdmin.createButton')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateAdminModal;
